import clsx from 'clsx';
import React from 'react';
import Input from '../../components/Input';
import Select from '../../components/Select';
import Button from '../../components/Button';
import { useButtonSizeLarge } from '../../utils/buttonSizes';
import { CompanySize, IndustryType } from '@EHSNavigator/backend';

interface SignUpCompanyDetailsProps {
  otherClasses?: string;
  signUpHandler: any;
}

const SignUpCompanyDetails: React.FC<SignUpCompanyDetailsProps> = ({
  otherClasses,
  signUpHandler,
}) => {
  const signUpCompanyDetailsClasses = clsx(
    otherClasses,
    'w-full flex flex-col items-center flex flex-col gap-5 md:gap-30 lg:gap-10',
  );
  const sizes = useButtonSizeLarge();

  return (
    <div
      className={signUpCompanyDetailsClasses}
      data-testid="sign-up-company-details"
      role="form"
      aria-label="Company Registration Form"
      aria-describedby="form-description"
    >
      <div className="grid w-full grid-cols-1 gap-5 md:gap-30 lg:grid-cols-2 lg:gap-10">
        <Input
          onBlur={signUpHandler.handleBlur}
          label="Company Name*"
          errorMessage={
            signUpHandler?.touched.companyName &&
            signUpHandler?.errors.companyName
              ? signUpHandler?.errors.companyName
              : ''
          }
          disabled={signUpHandler.isSubmitting}
          value={signUpHandler.values.companyName}
          type="text"
          placeholder="e.g. Globex ltd"
          onChange={signUpHandler.handleChange}
          name="companyName"
          otherClasses={{ container: 'w-full' }}
          aria-required={true}
          aria-invalid={
            signUpHandler?.touched.companyName &&
            signUpHandler?.errors.companyName
              ? true
              : false
          }
        />
        <Select
          label="Industry Type*"
          list={Object.entries(IndustryType).map(([, value]) => ({
            label: value,
            value: value,
          }))}
          value={signUpHandler.values.industryType}
          disabled={signUpHandler.isSubmitting}
          placeholder="Industry Types"
          otherClasses={{ container: 'w-full' }}
          onSelect={(e) =>
            signUpHandler?.setFieldValue('industryType', e, true)
          }
          errorMessage={
            signUpHandler?.touched.industryType &&
            signUpHandler?.errors.industryType
              ? signUpHandler?.errors.industryType
              : ''
          }
          aria-required={true}
          aria-invalid={
            signUpHandler?.touched.industryType &&
            signUpHandler?.errors.industryType
              ? true
              : false
          }
        />
      </div>
      <div className="grid w-full grid-cols-1 gap-5 md:gap-30 lg:grid-cols-2 lg:gap-10">
        <Select
          label="Industry Size*"
          list={Object.entries(CompanySize).map(([, value]) => ({
            label: value,
            value: value,
          }))}
          value={signUpHandler.values.industrySize}
          disabled={signUpHandler.isSubmitting}
          placeholder="e.g. 301+ employees"
          otherClasses={{ container: 'w-full' }}
          onSelect={(e) =>
            signUpHandler?.setFieldValue('industrySize', e, true)
          }
          errorMessage={
            signUpHandler?.touched.industrySize &&
            signUpHandler?.errors.industrySize
              ? signUpHandler?.errors.industrySize
              : ''
          }
        />
        <Input
          onBlur={signUpHandler.handleBlur}
          label="Headquarter Address*"
          errorMessage={
            signUpHandler?.touched.headquarterAddress &&
            signUpHandler?.errors.headquarterAddress
              ? signUpHandler?.errors.headquarterAddress
              : ''
          }
          disabled={signUpHandler.isSubmitting}
          value={signUpHandler.values.headquarterAddress}
          type="text"
          placeholder="e.g. California"
          onChange={signUpHandler.handleChange}
          name="headquarterAddress"
          otherClasses={{ container: 'w-full' }}
          aria-required={true}
          aria-invalid={
            signUpHandler?.touched.headquarterAddress &&
            signUpHandler?.errors.headquarterAddress
              ? true
              : false
          }
        />
      </div>
      <div className="flex w-full justify-start">
        <Input
          onBlur={signUpHandler.handleBlur}
          label={
            <>
              Organization Contact Number{' '}
              <em className="text-sm font-normal">(Optional)</em>
            </>
          }
          errorMessage={
            signUpHandler?.touched.organizationContact &&
            signUpHandler?.errors.organizationContact
              ? signUpHandler?.errors.organizationContact
              : ''
          }
          disabled={signUpHandler.isSubmitting}
          value={signUpHandler.values.organizationContact}
          type="tel"
          placeholder="Enter organization contact number"
          onChange={signUpHandler.handleChange}
          name="organizationContact"
          otherClasses={{ container: 'w-full max-w-465' }}
        />
      </div>
      <Button
        otherClasses="w-full max-w-465 mt-2 sm:mt-0"
        type="submit"
        size={sizes}
        loading={signUpHandler.isSubmitting}
        aria-label="Register Company"
        disable={signUpHandler.isSubmitting}
      >
        Register Company
      </Button>
    </div>
  );
};

export default SignUpCompanyDetails;
